// @tenorlab/nuxt-dashboard-pro
// file: app/widgets-catalog.ts
import { WidgetContainerColumn, WidgetContainerLarge, WidgetContainerRow } from '@tenorlab/vue-dashboard'
import {
  createStaticEntry,
  localWidgetDiscovery,
  // remoteWidgetDiscovery,
} from '@tenorlab/vue-dashboard/core'
// import { getWidgetsManifestUrl } from '@/utils/'

// other static widgets
import {
  WidgetSmallCardTransparentSample1,
  WidgetSmallCardTransparentSample2,
  WidgetSmallCardTransparentSample3,
  WidgetSmallCardTransparentSample4,
  WidgetSmallCardTransparentSample5,
} from './other-widgets/other-widgets'
import { otherWidgetsMetaMap } from './other-widgets/other-widgets-meta'
import type {
  IDynamicWidgetCatalogEntry,
  TDashboardWidgetCatalog,
  TWidgetMetaInfoBase,
  TWidgetFactory,
} from '@tenorlab/vue-dashboard'

// import { markRaw } from 'vue'
// import type { Component as VueComponent } from 'vue'
// import { staticWidgetsMetaMap } from './widgets-meta'

// NOTE: In Nuxt we cannot use '/src/bundled-widgets' or '/src/async-widgets' like in a Vue SPA:
const bundledWidgetsSrcPath = '/bundled-widgets'
const asyncWidgetsSrcPath = '/async-widgets'

// Use Vite's Glob Import
// This creates an object where the keys are file paths, and the values are the TWidgetFactory functions.
// We target the 'index.ts' files within the widgets subdirectories.
type TGlobModuleMap = Record<string, TWidgetFactory>

// Eagerly loaded (Non-lazy / Bundled):
// NOTE: In Nuxt we cannot use '/src/bundled-widgets' or '/src/async-widgets' like in a Vue SPA:
const bundledWidgetModules = import.meta.glob('/bundled-widgets/*/index.ts', {
  eager: true /* we load this immediately */,
}) as TGlobModuleMap

// Lazy loaded (Code-split / Plugins):
const asyncWidgetModules = import.meta.glob('/async-widgets/*/index.ts') as TGlobModuleMap

// Meta modules (Always eager so titles/icons are available immediately)
const allMetaModules = import.meta.glob('/**/widget-*/meta.ts', {
  eager: true,
}) as Record<string, Record<string, TWidgetMetaInfoBase>>

const hasPermission = (_user_: any, _permission: string) => true

// /**
//  * @name createStaticEntry
//  * Wraps around createStaticEntry from npm package to ensure the component added is marked raw (using markRaw)
//  */
// export const createStaticEntry = (
//   key: string,
//   component: VueComponent,
//   metaData?: TWidgetMetaInfoBase,
// ): [string, IDynamicWidgetCatalogEntry] => {
//   return _createStaticEntry(key, markRaw(component), metaData)
// }

/**
 * @name getWidgetCatalog
 * @description Dynamically builds the widgets catalog based on user type and operations/permissions.
 */
export const getWidgetCatalog = async (user: any | null): Promise<TDashboardWidgetCatalog> => {
  // A. Register Static Core Components
  const catalogMapEntries: [string, IDynamicWidgetCatalogEntry][] = [
    createStaticEntry('WidgetContainer', WidgetContainerColumn, otherWidgetsMetaMap['WidgetContainer']),
    createStaticEntry('WidgetContainerRow', WidgetContainerRow, otherWidgetsMetaMap['WidgetContainerRow']),
    createStaticEntry(
      'WidgetContainerLarge',
      WidgetContainerLarge,
      otherWidgetsMetaMap['WidgetContainerLarge'],
    ),
  ]

  // B. Register Business Static Widgets
  // we could filter further by permissions and user type if needed
  if (hasPermission(user, 'some-permission')) {
    // catalogMapEntries.push(createStaticEntry('WidgetRequiresPermissions', WidgetRequiresPermissions, otherWidgetsMetaMap['WidgetRequiresPermissions']))
  }

  // add widgets to which everyone has access:
  catalogMapEntries.push(
    createStaticEntry(
      'WidgetSmallCardTransparentSample1',
      WidgetSmallCardTransparentSample1,
      otherWidgetsMetaMap['WidgetSmallCardTransparentSample1'],
    ),
  )
  catalogMapEntries.push(
    createStaticEntry(
      'WidgetSmallCardTransparentSample2',
      WidgetSmallCardTransparentSample2,
      otherWidgetsMetaMap['WidgetSmallCardTransparentSample2'],
    ),
  )
  catalogMapEntries.push(
    createStaticEntry(
      'WidgetSmallCardTransparentSample3',
      WidgetSmallCardTransparentSample3,
      otherWidgetsMetaMap['WidgetSmallCardTransparentSample3'],
    ),
  )
  catalogMapEntries.push(
    createStaticEntry(
      'WidgetSmallCardTransparentSample4',
      WidgetSmallCardTransparentSample4,
      otherWidgetsMetaMap['WidgetSmallCardTransparentSample4'],
    ),
  )
  catalogMapEntries.push(
    createStaticEntry(
      'WidgetSmallCardTransparentSample5',
      WidgetSmallCardTransparentSample5,
      otherWidgetsMetaMap['WidgetSmallCardTransparentSample5'],
    ),
  )

  // add local bundled widgets (not-lazy)
  catalogMapEntries.push(
    ...localWidgetDiscovery(
      bundledWidgetsSrcPath,
      bundledWidgetModules,
      allMetaModules,
      false, // lazy: false
    ),
  )

  // Optional: add local async widgets (lazy)
  catalogMapEntries.push(
    ...localWidgetDiscovery(
      asyncWidgetsSrcPath,
      asyncWidgetModules,
      allMetaModules,
      true, // lazy: true
    ),
  )

  // // Optional: add remote widgets (hosted on CDN etc)
  // // this requires import maps and more complex setup (microfrontends, see the @tenorlab/vue-dashboard-pro)
  // const manifestUrl = getWidgetsManifestUrl()
  // if (manifestUrl.length > 0) {
  //   try {
  //     const remoteResponse = await remoteWidgetDiscovery(manifestUrl)
  //     if (!remoteResponse.message) {
  //       catalogMapEntries.push(...(remoteResponse.entries || []))
  //     } else {
  //       console.error('Remote plugin discovery failed:', remoteResponse.message, remoteResponse.details)
  //     }
  //   } catch (e) {}
  // } else {
  //   console.error(`manifestUrl (VITE_WIDGETS_MANIFEST_URL) not set. Skipping remote widget discovery`)
  // }

  return new Map(catalogMapEntries)
}
